/**
 * Click-to-play for listing videos (views/components/media-embed.twig).
 *
 * The page ships a poster image and a play button, not the player: a YouTube
 * or Vimeo iframe is ~0.5 MB of script before anyone presses anything. On play
 * the iframe is created with autoplay. When the video ENDS the iframe is
 * removed and the poster put back, which is the only reliable way to keep the
 * host's end screen (other people's videos, on YouTube) off a listing – no
 * player parameter removes it any more.
 *
 * End detection uses each host's postMessage protocol directly, so no player
 * SDK is loaded:
 *  - YouTube (enablejsapi=1): send {event:'listening'} after load; the player
 *    then posts infoDelivery messages, and playerState 0 is "ended".
 *  - Vimeo: send {method:'addEventListener', value:'ended'} after load; the
 *    player posts {event:'ended'}.
 */
const ORIGINS = {
    youtube: 'https://www.youtube-nocookie.com',
    vimeo: 'https://player.vimeo.com',
};

export function mediaEmbed() {
    const posters = document.querySelectorAll('[data-lh-embed-src]');

    if (!posters.length) {
        return;
    }

    const players = new Map(); // iframe.contentWindow -> { iframe, poster }

    posters.forEach((poster) => {
        poster.addEventListener('click', (e) => {
            e.preventDefault();

            const iframe = document.createElement('iframe');

            iframe.src = poster.dataset.lhEmbedSrc;
            iframe.title = poster.dataset.lhEmbedTitle || '';
            iframe.allow = 'autoplay; fullscreen; picture-in-picture';
            iframe.allowFullscreen = true;
            iframe.referrerPolicy = 'strict-origin-when-cross-origin';
            iframe.dataset.lhEmbed = poster.dataset.lhEmbedProvider || '';

            iframe.addEventListener('load', () => {
                players.set(iframe.contentWindow, { iframe, poster });
                subscribe(iframe, poster.dataset.lhEmbedProvider);
            });

            poster.hidden = true;
            poster.insertAdjacentElement('afterend', iframe);
        });
    });

    window.addEventListener('message', (event) => {
        const entry = players.get(event.source);

        if (!entry) {
            return;
        }

        const provider = entry.poster.dataset.lhEmbedProvider;

        // Vimeo drops a subscription sent before the player is ready, and
        // announces readiness with its own message – subscribe again on it.
        if (provider === 'vimeo' && parse(event, provider)?.event === 'ready') {
            subscribe(entry.iframe, provider);

            return;
        }

        // YouTube reports a deleted, private or embed-disabled video as an
        // error event (codes 100, 101, 150) and then shows "Video unavailable"
        // in the frame. Nothing is better than that: drop the whole block.
        if (provider === 'youtube' && parse(event, provider)?.event === 'onError') {
            players.delete(event.source);
            entry.iframe.closest('.lh-embed')?.remove();

            return;
        }

        if (!ended(event, provider)) {
            return;
        }

        players.delete(event.source);
        entry.iframe.remove();
        entry.poster.hidden = false;
    });
}

function subscribe(iframe, provider) {
    const origin = ORIGINS[provider];

    if (!origin || !iframe.contentWindow) {
        return;
    }

    if (provider === 'youtube') {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), origin);
    }

    if (provider === 'vimeo') {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'ended' }), origin);
    }
}

function parse(event, provider) {
    if (event.origin !== ORIGINS[provider]) {
        return null;
    }

    let data = event.data;

    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch {
            return null;
        }
    }

    return data && typeof data === 'object' ? data : null;
}

function ended(event, provider) {
    const data = parse(event, provider);

    if (!data) {
        return false;
    }

    if (provider === 'youtube') {
        return data.event === 'infoDelivery' && data.info && data.info.playerState === 0;
    }

    if (provider === 'vimeo') {
        return data.event === 'ended';
    }

    return false;
}
